// minimap-render.ts — the pure, DOM-free, node-testable radar painter (HTML/DOM UI
// shell plan, Phase 2 = forms.ini elimination). It is the verbatim extraction of the
// canvas MiniMapControl render: the per-tile painter RedrawTRect (game/SimUI.cpp:
// 3000-3093 / client/src/ui/simuiform.ts), the mini-tile color lookup (SimUI.cpp:2575
// / simuiform.ts miniTileColor), the dib border drawing (Init, SimUI.cpp:2611-2723),
// and the white viewport-box geometry (OnPaint, SimUI.cpp:2725-2781). NOTHING here
// touches the DOM, the form stack, or any global — the DOM <canvas> view (minimap-
// view.ts) feeds it a plain data bundle and blits the result. The byte-level golden
// (test/vectors/minimap/) pins this against the original MiniMapControl pixel output.
//
// PIXEL PRIORITY per tile (RedrawTRect): fog-opaque → black; else unit/structure blip
// (white if selected, side color); else wall; else galaxite; else the terrain mini-
// tile. The mini-tile dword byte order is [A,B,G,R] (the same LE RGBA8888 tilemap.ts
// decodes) — read as { r: b[o+3], g: b[o+2], b: b[o+1] }. Structures are painted HERE
// (they hit the blip branch — kfGobMobileUnit is 0 for a structure); the C++
// knLayerMiniMap structure pass (gobdraw.ts) is dead code in the TS port.

import { CpuDibBitmap } from '../../gfx/dibbitmap.ts';
import { GetColor, GetSideColor, iclr } from '../../gfx/palette.ts';
import type { Color } from '../../gfx/texatlasmgr.ts';
import { TcFromWc, WcFromTc } from '../../../../sim/src/math/coords.ts';
import { kwcTile } from '../../../../sim/src/gob/unitconsts.ts';

// ht.h:1369-1378 — the fog byte masks (lifted verbatim from simuiform.ts:78-82).
const kbfFogMask = 0x000f;
const kbOpaque = 0;
const IsFogOpaque = (bFog: number): boolean => (bFog & kbfFogMask) === kbOpaque;
const kbfGalaxiteMask = 0x00f0;
const HasGalaxite = (bFog: number): boolean => (bFog & kbfGalaxiteMask) !== 0;

// game/SimUI.cpp:3027 + ht.h gob flags — the wall + unit-blip predicates (verbatim
// from simuiform.ts:85 and the RedrawTRect gob test).
const kttWall = 2;
const HasWall = (btt: number): boolean => btt === kttWall;
const kfGobMobileUnit = 0x00000800;
const kfGobActive = 0x00000200;
const kfGobSelected = 0x00004000;

// ht.h:8689-8691 (kcSides).
const kcSides = 5;

/** A gob at a tile, as the minimap reads it (ctx.gobm.GetUnitGob → GetFlags/GetSide).
 *  null = no unit gob at that tile. */
export interface MiniMapGob {
  GetFlags(): number;
  GetSide(): number;
}

/** The plain data bundle the pure painter consumes — every field is a clean sim/tilemap
 *  getter the canvas MiniMapControl already read (NOT form-coupled). The view builds it
 *  each frame from the GameSession minimap accessors. */
export interface MiniMapData {
  /** ctx.gobm.GetMapSize() — the tile dimensions (m_ctx × m_cty). */
  ctx: number;
  cty: number;
  /** m_nScale — 1 or 2 minimap px per tile (MiniMapControl::Init scale calc). */
  nScale: number;
  /** tmap.m_pwMapData — the BE-decoded tile offsets (nTile = pw & 0x7fc). */
  tileMap: Uint16Array;
  /** tmap.m_miniTset — the mapped mini-tileset bytes (RGBA dwords). */
  miniTset: Uint8Array;
  /** GetMiniTileSetHeader(nScale).off + 8 — byte offset of the first mini-tile dword. */
  ibTileData: number;
  /** ctx.fogm.GetMapPtr() — the per-tile fog/galaxite bytes. */
  fogMap: Uint8Array;
  /** ctx.trmap.GetMapPtr() — the per-tile terrain-type bytes. */
  trMap: Uint8Array;
  /** ctx.gobm.GetUnitGob(tx, ty) — the unit gob occupying a tile (null = none). */
  gobAt(tx: number, ty: number): MiniMapGob | null;
}

/** The half-open tile rect RedrawTRect paints. */
export interface TileRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A minimal Rect for the viewport-box geometry (mirrors ui/form.ts Rect's fields). */
export interface RectLike {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** The minimap dib + its layout, computed once per (map, scale) — mirrors the canvas
 *  MiniMapControl::Init geometry (the even-width border alignment). */
export interface MiniMapSurface {
  /** the owned dib (sized exactly like the canvas m_pbm). */
  pbm: CpuDibBitmap;
  /** m_xOff / m_yOff — where the tile pixels start inside the dib (the border offset). */
  xOff: number;
  yOff: number;
  /** m_cxyBorder (=1). */
  cxyBorder: number;
  /** kfMmVertBorderInside — whether the vertical border is drawn INSIDE the dib (odd
   *  map width) or outside (even). Drives OnPaint's blit + the viewport box left edge. */
  vertBorderInside: boolean;
  /** the side/cyan border color (multiplayer = local side color, else cyan). */
  clrBorder: Color;
}

/** The minimap control's on-screen WIDTH/HEIGHT in back-buffer px — the canvas
 *  MiniMapControl rect size (the dib + border), game/SimUI.cpp:2655-2658. The placement
 *  dead-zone check (buildui.ts) + any screen-rect consumer use this, so it stays the
 *  SINGLE source of truth shared with the DOM view's surface geometry. cxyBorder = 1. */
export function miniMapControlSize(
  sizMapCx: number,
  sizMapCy: number,
  gcxTile: number,
  gcyTile: number,
  nScale: number,
): { cx: number; cy: number } {
  const cxyBorder = 1;
  return {
    cx: Math.trunc(sizMapCx / Math.trunc(gcxTile / nScale)) + cxyBorder,
    cy: Math.trunc(sizMapCy / Math.trunc(gcyTile / nScale)) + cxyBorder,
  };
}

/** The minimap's on-screen RECT, anchored bottom-right of the SIM strip — exactly
 *  Game::SizeAndPositionEverything (game.cpp:2084-2105): Set(sizDib.cx - mmW, cySim - mmH,
 *  sizDib.cx, cySim). The DOM radar floats over this same screen region, and the
 *  structure-placement dead zone (buildui.ts) must still exclude it, so both read this. */
export function miniMapScreenRect(
  sizDibCx: number,
  cySim: number,
  sizMapCx: number,
  sizMapCy: number,
  gcxTile: number,
  gcyTile: number,
  nScale: number,
): RectLike {
  const siz = miniMapControlSize(sizMapCx, sizMapCy, gcxTile, gcyTile, nScale);
  return { left: sizDibCx - siz.cx, top: cySim - siz.cy, right: sizDibCx, bottom: cySim };
}

/** game/SimUI.cpp:2575 — the mini-tile dword → Color. The tset dword byte order is
 *  [A,B,G,R] (the LE RGBA8888 layout tilemap.ts decodes). VERBATIM from
 *  MiniMapControl::miniTileColor. */
function miniTileColor(d: MiniMapData, idw: number): Color {
  const b = d.miniTset;
  const o = d.ibTileData + idw * 4;
  return { r: b[o + 3], g: b[o + 2], b: b[o + 1] } as Color;
}

/** Compute the minimap dib geometry + draw its borders — the canvas MiniMapControl::Init
 *  arm (SimUI.cpp:2611-2723). `sizMapPx` = tmap.GetMapSize() (pixels), `gcxTile`/`gcyTile`
 *  = the render tile pixel size (coords), `cyInputUI` = the input-strip dib height (for
 *  the bottom border arm), `fMultiplayer`/`sideLocal` pick the border color. Returns the
 *  surface; the caller then runs `redrawTRect` over the whole map to fill it.
 *
 *  The width-parity rule (verbatim): the map is aligned to an edge (even). If the map
 *  width is odd, the border is drawn INSIDE the dib (so the blt stays even-aligned); if
 *  even, outside. m_xOff/m_yOff follow. */
export function createMiniMapSurface(
  ctxTiles: number,
  ctyTiles: number,
  nScale: number,
  sizMapCx: number,
  sizMapCy: number,
  gcxTile: number,
  gcyTile: number,
  cyInputUI: number,
  fMultiplayer: boolean,
  sideLocal: number,
  newSurface: (cx: number, cy: number) => CpuDibBitmap,
): MiniMapSurface {
  const cxyBorder = 1;

  // Size (SimUI.cpp:2655-2658) — the control rect width/height. The control's right
  // edge is the form's right edge; we only need the WIDTH and HEIGHT here (the dib),
  // not the on-screen position. rcWidth = (sizMap.cx / (gcxTile/nScale)) + cxyBorder.
  const rcWidth = Math.trunc(sizMapCx / Math.trunc(gcxTile / nScale)) + cxyBorder;
  const rcHeight = Math.trunc(sizMapCy / Math.trunc(gcyTile / nScale)) + cxyBorder;

  // Even-alignment / border placement (SimUI.cpp:2660-2682).
  let cxDib = rcWidth;
  let vertBorderInside: boolean;
  let xOff: number;
  let yOff: number;
  if (cxDib & 1) {
    vertBorderInside = false;
    cxDib -= cxyBorder;
    xOff = 0;
    yOff = cxyBorder;
  } else {
    vertBorderInside = true;
    xOff = cxyBorder;
    yOff = cxyBorder;
  }

  const pbm = newSurface(cxDib, rcHeight);

  // The border color: multiplayer = local side color (gaiclrSide[side]); else cyan.
  const clrBorder = fMultiplayer ? GetSideColor(sideLocal) : GetColor(iclr.kiclr0CyanSide);

  // Draw internal borders (SimUI.cpp:2686-2706). x0 = y0 = 0. (cxyBorder is always 1 on
  // this host, so the C++ `if (m_cxyBorder != 0)` guard is unconditional here.)
  const x0 = 0;
  const y0 = 0;
  if (vertBorderInside) {
    pbm.Fill(x0 + cxyBorder, y0, rcWidth - cxyBorder, cxyBorder, clrBorder);
    pbm.Fill(x0, y0, cxyBorder, rcHeight - cyInputUI + 1, clrBorder);
    pbm.Fill(x0, y0 + rcHeight - cyInputUI + 1, cxyBorder, rcHeight, GetColor(iclr.kiclrBlack));
  } else {
    pbm.Fill(x0, y0, rcWidth, cxyBorder, clrBorder);
  }

  return { pbm, xOff, yOff, cxyBorder, vertBorderInside, clrBorder };
}

/** SubBitmap-at-(xOff,yOff) Fill (SimUI.cpp:3018-3020) — the minimap dib's tile writes
 *  are offset by the border. VERBATIM from MiniMapControl::fillDst. */
function fillDst(
  surf: MiniMapSurface,
  x: number,
  y: number,
  cx: number,
  cy: number,
  clr: Color,
): void {
  surf.pbm.Fill(x + surf.xOff, y + surf.yOff, cx, cy, clr);
}

/** The per-tile radar painter — game/SimUI.cpp:3000-3093 / MiniMapControl::RedrawTRect,
 *  lifted VERBATIM. Paints `ptrc` (half-open tile rect) into `surf.pbm`. `fHasPoweredRadar`
 *  gates the enemy-unit blips: the canvas RedrawTRect always runs the same code, but the
 *  fog/blip outcome already reflects radar via the fog reveal + CalcPoweredRadar's full
 *  Redraw on a power change — see note below. */
export function redrawTRect(d: MiniMapData, surf: MiniMapSurface, ptrc: TileRect): void {
  const offset = ptrc.top * d.ctx + ptrc.left;
  let iFog = offset;
  let iwMap = offset;
  let iTr = offset;
  const ctReturn = d.ctx - (ptrc.right - ptrc.left);

  // The per-side / fixed colors (MiniMapControl::Init constants, SimUI.cpp:2708-2716).
  const clrBlack = GetColor(iclr.kiclrBlack);
  const clrWhite = GetColor(iclr.kiclrWhite);
  const clrGalaxite = GetColor(iclr.kiclrGalaxite);
  const clrWall = GetColor(iclr.kiclrSideNeutral);
  const aclrSide: Color[] = [];
  for (let sideT = 0; sideT < kcSides; sideT++) aclrSide[sideT] = GetSideColor(sideT);

  const nScale = d.nScale;

  for (let ty = ptrc.top; ty < ptrc.bottom; ty++) {
    for (let tx = ptrc.left; tx < ptrc.right; tx++, iFog++, iwMap++, iTr++) {
      // Fogged?
      if (IsFogOpaque(d.fogMap[iFog])) {
        fillDst(surf, tx * nScale, ty * nScale, nScale, nScale, clrBlack);
        continue;
      }

      // Unit gob?
      const punt = d.gobAt(tx, ty);
      if (punt !== null) {
        const wf = punt.GetFlags();
        if ((wf & (kfGobMobileUnit | kfGobActive)) !== kfGobMobileUnit) {
          let clr: Color;
          if (wf & kfGobSelected) {
            clr = clrWhite;
          } else {
            clr = aclrSide[punt.GetSide()];
          }
          fillDst(surf, tx * nScale, ty * nScale, nScale, nScale, clr);
          continue;
        }
      }

      // Wall?
      if (HasWall(d.trMap[iTr])) {
        fillDst(surf, tx * nScale, ty * nScale, nScale, nScale, clrWall);
        continue;
      }

      // Galaxite?
      if (HasGalaxite(d.fogMap[iFog])) {
        fillDst(surf, tx * nScale, ty * nScale, nScale, nScale, clrGalaxite);
        continue;
      }

      // Tile
      const nTile = d.tileMap[iwMap] & 0x7fc; // BigWord(*pwTileMap) & 0x7fc
      if (nScale === 1) {
        fillDst(surf, tx, ty, 1, 1, miniTileColor(d, nTile >> 2));
      } else {
        // Scale 2: each tile's mini image is a 2x2 pixel block (4 dwords at nTile).
        fillDst(surf, tx * 2, ty * 2, 1, 1, miniTileColor(d, nTile));
        fillDst(surf, tx * 2 + 1, ty * 2, 1, 1, miniTileColor(d, nTile + 1));
        fillDst(surf, tx * 2, ty * 2 + 1, 1, 1, miniTileColor(d, nTile + 2));
        fillDst(surf, tx * 2 + 1, ty * 2 + 1, 1, 1, miniTileColor(d, nTile + 3));
      }
    }
    iwMap += ctReturn;
    iFog += ctReturn;
    iTr += ctReturn;
  }
}

/** Repaint the WHOLE map (MiniMapControl::Redraw, SimUI.cpp:2837-2842). */
export function redrawAll(d: MiniMapData, surf: MiniMapSurface): void {
  redrawTRect(d, surf, { left: 0, top: 0, right: d.ctx, bottom: d.cty });
}

/** The coords the viewport-box + the OnPaint blit need (a subset of CoordMap +
 *  the playfield size + the current view world position). */
export interface MiniMapViewGeom {
  gcxTile: number;
  gcyTile: number;
  /** coords.PcFromUwc — world → unsigned playfield px (table). */
  PcFromUwc(wc: number): number;
  /** coords.WcFromUpc — playfield px → unsigned world (table). */
  WcFromUpc(pc: number): number;
  /** the playfield (sim strip) size in px. */
  playfieldCx: number;
  playfieldCy: number;
  /** the current view world position (sui.GetViewPos). */
  wxView: number;
  wyView: number;
}

/** The white viewport box, in MINIMAP-PIXEL coordinates relative to the dib's top-left
 *  (the canvas MiniMapControl::OnPaint computes it in BACK-BUFFER coords at x=m_rc.left+
 *  rcForm.left; here the DOM canvas IS the dib, so we drop the screen origin and the
 *  blit-side cxyBorder offset is folded into rc.left below to match OnPaint exactly).
 *  Returns the box rect clamped to the dib bounds — DrawBorder(1px white) draws it. */
export function viewportBox(g: MiniMapViewGeom, surf: MiniMapSurface, nScale: number): RectLike {
  // OnPaint (SimUI.cpp:2745-2762): xView/yView = PcFromUwc(wxView), then snap through
  // WcFromUpc → PcFromUwc (the even-pixel view origin), then / cxTile per minimap px.
  const xView = g.PcFromUwc(g.wxView);
  const yView = g.PcFromUwc(g.wyView);
  const wxView = g.WcFromUpc(xView);
  const wyView = g.WcFromUpc(yView);

  const cxTile = Math.trunc(g.gcxTile / nScale);
  const cyTile = Math.trunc(g.gcyTile / nScale);

  // The dib-local box: the canvas adds (x + cxyBorder) for the screen origin; here the
  // dib's tile pixels start at xOff=cxyBorder (odd width: xOff=0 but the blit adds it),
  // so the box's left/top in dib coords = cxyBorder + (px / tile). We express it relative
  // to the dib's pixel (0,0); the view fills the canvas one-to-one with the dib.
  const rc: RectLike = { left: 0, top: 0, right: 0, bottom: 0 };
  rc.left = surf.cxyBorder + Math.trunc(g.PcFromUwc(wxView) / cxTile);
  rc.top = surf.cxyBorder + Math.trunc(g.PcFromUwc(wyView) / cyTile);
  rc.right = rc.left + Math.trunc((g.playfieldCx + cxTile - 1) / cxTile);
  rc.bottom = rc.top + Math.trunc((g.playfieldCy + cyTile - 1) / cyTile);

  // Clamp to the dib bounds (SimUI.cpp:2766-2769) — when the whole map fits on screen,
  // the playfield is larger than the map so the box would spill past the edges.
  const siz = surf.pbm.GetSize();
  rc.left = Math.max(rc.left, 0);
  rc.top = Math.max(rc.top, 0);
  rc.right = Math.min(rc.right, siz.cx);
  rc.bottom = Math.min(rc.bottom, siz.cy);
  return rc;
}

//
// Input — the click-to-jump + drag-scroll geometry, lifted VERBATIM from
// MiniMapControl::OnPenEvent2 (game/SimUI.cpp:2883-2958 / simuiform.ts:1344-1402).
// The view feeds MINIMAP-PIXEL pen coords (xPen, yPen) — relative to the minimap dib's
// TILE origin (i.e. with the cxyBorder already subtracted, like the C++ which subtracts
// rcForm.left + m_rc.left + m_cxyBorder). The pure fn returns the new world view position
// (the view calls setViewPos with it).
//

/** The per-gesture statics OnPenEvent2 stashes between pen-down and the following moves
 *  (SimUI.cpp:2885-2888) — the view owns one of these per active drag. */
export interface MiniMapPenState {
  xPenDown: number;
  yPenDown: number;
  wxViewDown: number;
  wyViewDown: number;
}

/** The geometry the input math needs (a subset of CoordMap + the playfield + the current
 *  view world position + the scale). */
export interface MiniMapInputGeom {
  gcxTile: number;
  gcyTile: number;
  nScale: number;
  playfieldCx: number;
  playfieldCy: number;
  /** the current view world position (sui.GetViewPos). */
  wxView: number;
  wyView: number;
}

/** A world view position. */
export interface WorldPos {
  wx: number;
  wy: number;
}

/** Pen DOWN — SimUI.cpp:2885-2932. If the tap is OUTSIDE the current view rect, recenter
 *  the view on the tapped tile; otherwise keep the current view (grab-to-drag). Records
 *  the gesture statics into `pen` for the following moves. Returns the new view world pos.
 *  `xPen`/`yPen` are minimap-pixel coords (border-relative, the C++ post-subtraction). */
export function minimapPenDown(
  g: MiniMapInputGeom,
  xPen: number,
  yPen: number,
  pen: MiniMapPenState,
): WorldPos {
  let wxView = g.wxView;
  let wyView = g.wyView;

  // The view rect in minimap-pixel coords (SimUI.cpp:2899-2908).
  const rcViewLeft = TcFromWc(wxView) * g.nScale;
  const rcViewTop = TcFromWc(wyView) * g.nScale;
  const rcViewRight =
    rcViewLeft + Math.trunc((g.playfieldCx + g.gcxTile - 1) / g.gcxTile) * g.nScale;
  const rcViewBottom =
    rcViewTop + Math.trunc((g.playfieldCy + g.gcyTile - 1) / g.gcyTile) * g.nScale;

  // Pen down inside the view rect? (Rect::PtIn — half-open.)
  const inView =
    xPen >= rcViewLeft && xPen < rcViewRight && yPen >= rcViewTop && yPen < rcViewBottom;
  if (!inView) {
    // Center the view on the tapped tile (SimUI.cpp:2913-2922).
    const tx = Math.trunc(xPen / g.nScale);
    const ty = Math.trunc(yPen / g.nScale);
    wxView = WcFromTc(tx - Math.trunc(Math.trunc(g.playfieldCx / g.gcxTile) / 2));
    wyView = WcFromTc(ty - Math.trunc(Math.trunc((g.playfieldCy + g.gcyTile - 1) / g.gcyTile) / 2));
  }

  pen.xPenDown = xPen;
  pen.yPenDown = yPen;
  pen.wxViewDown = wxView;
  pen.wyViewDown = wyView;
  return { wx: wxView, wy: wyView };
}

/** Pen MOVE (drag) — SimUI.cpp:2933-2942. Scrolls the view by the pen delta since the
 *  pen-down, in world units (whole-tile via WcFromTc + the sub-tile remainder). */
export function minimapPenMove(
  g: MiniMapInputGeom,
  xPen: number,
  yPen: number,
  pen: MiniMapPenState,
): WorldPos {
  const dx = xPen - pen.xPenDown;
  let wxView = pen.wxViewDown + WcFromTc(Math.trunc(dx / g.nScale));
  wxView += Math.trunc(((dx % g.nScale) * kwcTile) / g.nScale);
  const dy = yPen - pen.yPenDown;
  let wyView = pen.wyViewDown + WcFromTc(Math.trunc(dy / g.nScale));
  wyView += Math.trunc(((dy % g.nScale) * kwcTile) / g.nScale);
  return { wx: wxView, wy: wyView };
}
